import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, FileText, Send, Sparkles, Shield, Clock, BarChart3, Download, Eye } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../common/Toast';
import ProjectIntelligenceWorkspaceShell from './ProjectIntelligenceWorkspaceShell';
import { getPiWorkspacePath } from './projectIntelligencePhases';
import '../../styles/dpr-premium.css';

export default function DprFinalisation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (id) fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/sourcing/project-requirements/${id}`);
      if (res.data.success) {
        setProject(res.data.data);
      } else {
        setProject(null);
      }
    } catch (err) {
      error('Failed to load project');
      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAndMove = async () => {
    try {
      setMoving(true);
      await api.put(`/sourcing/project-requirements/${id}/finalisation`);
      const res2 = await api.put(`/sourcing/project-requirements/${id}/move-to-prerfp`);
      if (res2.data.success) {
        const preRftProjectId = res2.data?.preRftProjectId;
        if (preRftProjectId) {
          success('DPR approved! Pre-RFP workflow created and pre-filled from your DPR.');
          navigate(`/sourcing/pre-rft/project/${preRftProjectId}?phase=identificationOfNeed`);
        } else {
          success('DPR approved and moved to Pre-RFP.');
          navigate('/sourcing/pre-rft');
        }
      }
    } catch (err) {
      error('Failed to approve DPR');
    } finally {
      setMoving(false);
    }
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const hasMissingData = () => {
    if (!project) return false;
    const compliances = project.compliances || [];
    return compliances.some(c => c.status === 'Pending');
  };

  const sections = project?.dprSections || [];
  const compliances = project?.compliances || [];
  const obtainedCount = compliances.filter(c => c.status === 'Obtained').length;
  const pendingCount = compliances.filter(c => c.status === 'Pending').length;

  const headerActions = project
    ? [
        {
          label: 'Export PDF',
          icon: <Download size={16} />,
          variant: 'outline',
          onClick: handleDownloadPDF,
        },
        {
          label: moving ? 'Processing...' : 'Approve & Move to Pre-RFP',
          icon: <Send size={16} />,
          variant: 'primary',
          onClick: handleApproveAndMove,
          disabled: moving || hasMissingData(),
        },
      ]
    : [];

  return (
    <ProjectIntelligenceWorkspaceShell
      phase="finalisation"
      projectId={id}
      projectTitle={project?.projectTitle}
      loading={loading}
      notFound={!loading && !project}
      actions={headerActions}
    >
      {hasMissingData() && (
        <div className="pi-alert-banner mb-4 d-flex flex-wrap align-items-center gap-3">
          <AlertTriangle size={22} className="text-warning flex-shrink-0" />
          <div className="flex-grow-1">
            <div className="fw-semibold">Pending regulatory clearances</div>
            <div className="small text-muted">
              {pendingCount} compliance item{pendingCount !== 1 ? 's' : ''} still pending. Resolve them or mark as N/A before approving.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-warning"
            onClick={() => navigate(getPiWorkspacePath('regulatory', id))}
          >
            Review
          </button>
        </div>
      )}

      <div className="pi-card pi-card-body mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h3 className="fw-bold mb-1">{project?.projectTitle}</h3>
            <div className="d-flex flex-wrap align-items-center gap-2 text-muted small">
              <span>{project?.projectType || project?.category || 'Uncategorized'}</span>
              {project?.location && (
                <>
                  <span>•</span>
                  <span>{project.location}</span>
                </>
              )}
              {project?.estimatedValue && (
                <>
                  <span>•</span>
                  <span>₹{project.estimatedValue}</span>
                </>
              )}
            </div>
          </div>
          <span className="pi-badge pi-badge--secondary">Step 4 of 4</span>
        </div>
        {project?.problemStatement && (
          <p className="text-muted mb-0">{project.problemStatement}</p>
        )}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-3 col-6">
          <div className="pi-stat-card text-center">
            <BarChart3 size={20} className="text-primary mb-2" />
            <div className="fw-bold fs-4">{sections.length}</div>
            <div className="text-muted small text-uppercase">DPR sections</div>
          </div>
        </div>
        <div className="col-md-3 col-6">
          <div className="pi-stat-card text-center">
            <FileText size={20} className="text-primary mb-2" />
            <div className="fw-bold fs-4">{project?.contributions?.length || 0}</div>
            <div className="text-muted small text-uppercase">Source files</div>
          </div>
        </div>
        <div className="col-md-3 col-6">
          <div className="pi-stat-card text-center">
            <CheckCircle size={20} className="text-success mb-2" />
            <div className="fw-bold fs-4">{obtainedCount}</div>
            <div className="text-muted small text-uppercase">Clearances obtained</div>
          </div>
        </div>
        <div className="col-md-3 col-6">
          <div className="pi-stat-card text-center">
            <Clock size={20} className={`mb-2 ${pendingCount > 0 ? 'text-warning' : 'text-success'}`} />
            <div className="fw-bold fs-4">{pendingCount}</div>
            <div className="text-muted small text-uppercase">Pending</div>
          </div>
        </div>
      </div>

      {sections.length > 0 && (
        <div className="pi-card overflow-hidden mb-4">
          <div className="pi-card-body border-bottom d-flex justify-content-between align-items-center py-3">
            <h6 className="m-0 fw-bold d-flex align-items-center gap-2">
              <Eye size={18} className="text-primary" /> DPR section summary
            </h6>
            <button
              type="button"
              className="btn btn-sm btn-link text-primary fw-medium text-decoration-none"
              onClick={() => navigate(getPiWorkspacePath('builder', id))}
            >
              Edit in Builder →
            </button>
          </div>
          <div>
            {sections.map((s, i) => (
              <div
                key={i}
                className="d-flex align-items-center gap-3 px-4 py-3"
                style={{ borderBottom: i < sections.length - 1 ? '1px solid #f1f5f9' : 'none' }}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold small text-primary"
                  style={{ width: 32, height: 32, background: 'rgba(116, 134, 214, 0.12)' }}
                >
                  {i + 1}
                </div>
                <div className="flex-grow-1 fw-semibold">{s.title}</div>
                {s.aiFlag ? (
                  <span className="pi-badge pi-badge--warning">
                    <AlertTriangle size={12} /> Needs review
                  </span>
                ) : (
                  <span className="pi-badge pi-badge--success">
                    <CheckCircle size={12} /> Ready
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {compliances.length > 0 && (
        <div className="pi-card overflow-hidden mb-4">
          <div className="pi-card-body border-bottom d-flex justify-content-between align-items-center py-3">
            <h6 className="m-0 fw-bold d-flex align-items-center gap-2">
              <Shield size={18} className="text-primary" /> Regulatory matrix
            </h6>
            <button
              type="button"
              className="btn btn-sm btn-link text-primary fw-medium text-decoration-none"
              onClick={() => navigate(getPiWorkspacePath('regulatory', id))}
            >
              Edit matrix →
            </button>
          </div>
          <div>
            {compliances.map((c, i) => (
              <div
                key={i}
                className="d-flex align-items-center gap-3 px-4 py-3"
                style={{ borderBottom: i < compliances.length - 1 ? '1px solid #f1f5f9' : 'none' }}
              >
                <div className="flex-shrink-0">
                  {c.status === 'Obtained' && <CheckCircle size={18} className="text-success" />}
                  {c.status === 'Pending' && <Clock size={18} className="text-warning" />}
                  {c.status === 'Not Applicable' && <span className="text-secondary">—</span>}
                </div>
                <div className="flex-grow-1">
                  <div className="fw-medium">{c.clearanceName || c.name}</div>
                  <div className="text-muted small">{c.authority}</div>
                </div>
                <span
                  className={`pi-badge ${
                    c.status === 'Obtained'
                      ? 'pi-badge--success'
                      : c.status === 'Not Applicable'
                        ? 'pi-badge--secondary'
                        : 'pi-badge--warning'
                  }`}
                >
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasMissingData() && (
        <div className="pi-alert-banner pi-alert-banner--success text-center mb-4 py-4">
          <CheckCircle size={28} className="text-success mb-2" />
          <h5 className="fw-bold mb-1">Ready for approval</h5>
          <p className="text-muted mb-0 small">
            All DPR sections and compliance items are verified. Click &quot;Approve &amp; Move to Pre-RFP&quot; to proceed.
          </p>
        </div>
      )}

      {sections.length > 0 && (
        <div className="pi-card overflow-hidden mb-4 dpr-print-container document-preview-sheet">
          <div className="p-4 px-5 d-flex flex-column align-items-center border-bottom">
            <h2 className="fw-bold text-center mb-1">{project?.projectTitle}</h2>
            <div className="text-muted text-uppercase small" style={{ letterSpacing: '2px' }}>
              Detailed Project Report
            </div>
          </div>

          <div className="p-5" style={{ lineHeight: 1.8 }}>
            {sections.map((s, i) => (
              <div key={i} className="mb-5">
                <h4 className="fw-bold mb-3 pb-2 text-primary border-bottom">
                  {i + 1}. {s.title}
                </h4>
                <div
                  className="dpr-content-html"
                  dangerouslySetInnerHTML={{
                    __html: s.content || '<p class="text-muted fst-italic">No content provided for this section.</p>',
                  }}
                />
              </div>
            ))}
          </div>

          {compliances.length > 0 && (
            <div className="p-5 pt-0">
              <h4 className="fw-bold mb-4 pb-2 text-primary border-bottom">
                Annexure A: Regulatory &amp; Compliance Matrix
              </h4>
              <table className="table table-bordered table-striped table-sm">
                <thead className="table-light">
                  <tr>
                    <th>Clearance name</th>
                    <th>Responsible authority</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {compliances.map((c, i) => (
                    <tr key={i}>
                      <td className="fw-medium">{c.clearanceName || c.name}</td>
                      <td>{c.authority || '-'}</td>
                      <td className={c.status === 'Obtained' ? 'text-success fw-bold' : ''}>{c.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ProjectIntelligenceWorkspaceShell>
  );
}
